/**
 * Shared normalization and whole-word matching for every lexicon that scans
 * free label text: nutrition/health claims (`nutrition-claim-lexicon.ts`),
 * reserved dairy names (`reserved-dairy-names.ts`) and reserved regime terms
 * (`reserved-term-lexicon.ts`, biologico/DOP/IGP/STG/senza glutine).
 *
 * Extracted because the three lexica had grown two independent — and
 * differently broken — implementations of the same idea: `foldCase` in
 * `nutrition-claim-lexicon.ts` did not know about exception phrases, and
 * `normalizeForMatching`/`containsWholeWordPhrase` in `reserved-dairy-names.ts`
 * used `[^a-z0-9]` as a word boundary, which splits on accented letters and
 * would false-positive after folding removes them (`"caffè"` folds to
 * `"caffe"`, but `[^a-z0-9]` still treats the pre-fold `è` as a boundary
 * character, which is inconsistent once matching runs on folded text).
 */

/**
 * NFD, diacritics removed, lowercase the way an Italian reader would read
 * it, every run of whitespace (including a line break) collapsed to one
 * space, trimmed. Without the whitespace pass a multi-word reserved
 * locution ("senza glutine", "in conversione", "denominazione di origine
 * protetta") matched only text with EXACTLY one ASCII space between its
 * words — a label wrapped across two lines in the layout editor, or a
 * stray double space, let the wording print unchecked.
 */
export function foldForMatching(text: string): string {
  return text
    .normalize('NFD')
    .replace(/\p{Diacritic}/gu, '')
    .toLocaleLowerCase('it-IT')
    .replace(/\s+/gu, ' ')
    .trim();
}

/**
 * true if `phrase` occurs in `haystack` as a whole word or locution. Boundary
 * is `\p{L}\p{N}`: "biodegradabile" does not contain the word "bio", but
 * "Bio-Yogurt" does — a hyphen is not a letter or digit, so it is a boundary.
 * Both arguments must already be folded with `foldForMatching`.
 */
export function containsWholeWordPhrase(haystack: string, phrase: string): boolean {
  const escaped = phrase.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
  return new RegExp(`(?<![\\p{L}\\p{N}])${escaped}(?![\\p{L}\\p{N}])`, 'u').test(haystack);
}

/**
 * Subtracts every phrase in `phrases` from `haystack`, replacing each
 * occurrence with a space so a term inside it never matches afterwards
 * ("valore biologico" removed from "alto valore biologico" must not leave
 * "alto  biologico" collapsing back into a match). `phrases` must already be
 * folded with `foldForMatching`.
 */
export function removePhrases(haystack: string, phrases: readonly string[]): string {
  return phrases.reduce((remaining, phrase) => {
    if (phrase.length === 0) {
      return remaining;
    }
    const escaped = phrase.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
    return remaining.replace(
      new RegExp(`(?<![\\p{L}\\p{N}])${escaped}(?![\\p{L}\\p{N}])`, 'gu'),
      ' ',
    );
  }, haystack);
}
